import { EEA_COUNTRIES, GCC_COUNTRIES } from '@/i18n/country'
import type { Currency } from '@/types/global'
import { CurrencyType } from '@/types/global'
import { buildCoinIconUrlBySourceSlug, buildFiatIconUrlBySourceSlug } from '@/utils/url'
/**
 * I check out these currency below
 * they use the code for price/[slug] Header Component,
 * take BTCCurrency as the default currency
 */
export const BTCCurrency: Currency<'BTC', ''> & { slug: string } = {
  code: 'BTC',
  name: 'Bitcoin',
  symbol: '',
  slug: 'bitcoin',
  iconUrl: buildCoinIconUrlBySourceSlug('bitcoin'),
  type: CurrencyType.Crypto,
}

export const CROCurrency: Currency<'CRO', ''> & { slug: string } = {
  code: 'CRO',
  name: 'Cronos',
  symbol: '',
  slug: 'cronos',
  iconUrl: buildCoinIconUrlBySourceSlug('cronos'),
  type: CurrencyType.Crypto,
}

export const ETHCurrency: Currency<'ETH', ''> & { slug: string } = {
  code: 'ETH',
  name: 'Ethereum',
  symbol: '',
  slug: 'ethereum',
  iconUrl: buildCoinIconUrlBySourceSlug('ethereum'),
  type: CurrencyType.Crypto,
}

export const SOLCurrency: Currency<'SOL', ''> & { slug: string } = {
  code: 'SOL',
  name: 'Solana',
  slug: 'solana',
  symbol: '',
  iconUrl: buildCoinIconUrlBySourceSlug('solana'),
  type: CurrencyType.Crypto,
}

export const cryptocurrencies: Array<Currency & { slug: string }> = [
  BTCCurrency,
  CROCurrency,
  ETHCurrency,
  SOLCurrency,
]

export const USDCurrency: Currency<'USD', '$'> = {
  code: 'USD',
  name: 'United States Dollar',
  nameKey: 'currency_usd_title',
  symbol: '$',
  dollarPrefix: 'US',
  iconUrl: buildFiatIconUrlBySourceSlug('USD'),
  type: CurrencyType.Fiat,
}

export const AUDCurrency: Currency<'AUD', '$'> = {
  code: 'AUD',
  name: 'Australian Dollar',
  nameKey: 'currency_aud_title',
  symbol: '$',
  dollarPrefix: 'A',
  iconUrl: buildFiatIconUrlBySourceSlug('AUD'),
  type: CurrencyType.Fiat,
}

export const BRLCurrency: Currency<'BRL', 'R$'> = {
  code: 'BRL',
  name: 'Brazilian Real',
  nameKey: 'currency_brl_title',
  symbol: 'R$',
  iconUrl: buildFiatIconUrlBySourceSlug('BRL'),
  type: CurrencyType.Fiat,
}

export const CADCurrency: Currency<'CAD', '$'> = {
  code: 'CAD',
  name: 'Canadian Dollar',
  nameKey: 'currency_cad_title',
  symbol: '$',
  dollarPrefix: 'CA',
  iconUrl: buildFiatIconUrlBySourceSlug('CAD'),
  type: CurrencyType.Fiat,
}

export const EURCurrency: Currency<'EUR', '€'> = {
  code: 'EUR',
  name: 'Euro',
  nameKey: 'currency_eur_title',
  symbol: '€',
  iconUrl: buildFiatIconUrlBySourceSlug('EUR'),
  type: CurrencyType.Fiat,
}

export const GBPCurrency: Currency<'GBP', '£'> = {
  code: 'GBP',
  name: 'British Pound',
  nameKey: 'currency_gbp_title',
  symbol: '£',
  iconUrl: buildFiatIconUrlBySourceSlug('GBP'),
  type: CurrencyType.Fiat,
}

export const HKDCurrency: Currency<'HKD', '$'> = {
  code: 'HKD',
  name: 'Hong Kong Dollar',
  nameKey: 'currency_hkd_title',
  symbol: '$',
  dollarPrefix: 'HK',
  iconUrl: buildFiatIconUrlBySourceSlug('HKD'),
  type: CurrencyType.Fiat,
}

export const RUBCurrency: Currency<'RUB', '₽'> = {
  code: 'RUB',
  name: 'Russian Ruble',
  nameKey: 'currency_rub_title',
  symbol: '₽',
  iconUrl: buildFiatIconUrlBySourceSlug('RUB'),
  type: CurrencyType.Fiat,
}

export const SGDCurrency: Currency<'SGD', '$'> = {
  code: 'SGD',
  name: 'Singapore dollar',
  nameKey: 'currency_sgd_title',
  symbol: '$',
  dollarPrefix: 'SG',
  iconUrl: buildFiatIconUrlBySourceSlug('SGD'),
  type: CurrencyType.Fiat,
}

export const TWDCurrency: Currency<'TWD', '$'> = {
  code: 'TWD',
  name: 'New Taiwan dollar',
  nameKey: 'currency_twd_title',
  symbol: '$',
  dollarPrefix: 'NT',
  iconUrl: buildFiatIconUrlBySourceSlug('TWD'),
  type: CurrencyType.Fiat,
}

export const KRWCurrency: Currency<'KRW', '₩'> = {
  code: 'KRW',
  name: 'Korean Won',
  nameKey: 'currency_krw_title',
  symbol: '₩',
  iconUrl: buildFiatIconUrlBySourceSlug('KRW'),
  type: CurrencyType.Fiat,
}

export const fiatCurrencies: Currency[] = [
  USDCurrency,
  AUDCurrency,
  BRLCurrency,
  CADCurrency,
  EURCurrency,
  GBPCurrency,
  HKDCurrency,
  RUBCurrency,
  SGDCurrency,
  TWDCurrency,
  KRWCurrency,
]

/**
 * from cloudflare cf-ipcountry, Alpha2 code
 */
type CountryCode = string

type CurrencyCode = string

const currencyCountryCodeMap: Record<CurrencyCode, CountryCode[]> = {
  [USDCurrency.code]: ['US', ...GCC_COUNTRIES],
  [EURCurrency.code]: ['FR', 'IT', 'TR', ...EEA_COUNTRIES],
  [AUDCurrency.code]: ['AU'],
  [BRLCurrency.code]: ['BR'],
  [CADCurrency.code]: ['CA'],
  [KRWCurrency.code]: ['KR'],
  [GBPCurrency.code]: ['GB'],
  [HKDCurrency.code]: ['HK'],
  [RUBCurrency.code]: ['RU'],
  [SGDCurrency.code]: ['SG'],
  [TWDCurrency.code]: ['TW'],
}

const countryCodeCurrencyMap: Record<CountryCode, CurrencyCode> = (() => {
  const map: Record<CountryCode, CurrencyCode> = {}
  for (const [currencyCode, countryCodes] of Object.entries(currencyCountryCodeMap)) {
    for (const countryCode of countryCodes) {
      map[countryCode] = currencyCode
    }
  }
  return map
})()

export const DEFAULT_CURRENCY = USDCurrency
// export const currencyOptions: Currency[] = [
export const supportedCurrencies = fiatCurrencies

export const allCurrencies = [...cryptocurrencies, ...fiatCurrencies]

export function getCurrencyInfo(currencyCode: string): Currency | undefined {
  return allCurrencies.find(currency => currency.code === currencyCode)
}

export function getCurrencyByCountryCode(countryCode: CountryCode): CurrencyCode {
  return countryCodeCurrencyMap[countryCode] || DEFAULT_CURRENCY.code
}
